import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { navItems, NavItem, NavChildItem, NavSubItem } from './data'
import EdcLogo from '../assets/edcLogo.jpg'
import govtLogo from '../assets/govt.png'
import UserIcon from '../components/common/icons/UserIcon'
import HamburgerIcon from '../components/common/icons/HamburgerIcon'
import Close from '../components/common/icons/Close'
import { router_config } from '../configs/route.config'
import UpArrow from '../components/common/icons/UpArrow'
import DownArrow from '../components/common/icons/DownArrow'
import { useAuthStore } from '../store/authStore'

const MiddleHeader: React.FC = () => {
  const { language, authenticated } = useAuthStore()
  const navigate = useNavigate()
  const navRef = useRef<HTMLDivElement>(null)
  const userIconRef = useRef<HTMLDivElement>(null)

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null
  )
  const [expandedParentIndex, setExpandedParentIndex] = useState<number | null>(
    null
  )
  const [expandedChildIndex, setExpandedChildIndex] = useState<number | null>(
    null
  )
  const [openChildIndex, setOpenChildIndex] = useState<{
    [key: number]: number | null
  }>({})
  const [showUserMenu, setShowUserMenu] = useState(false)

  const getLabel = (item: NavItem | NavChildItem | NavSubItem) => {
    return language === 'lang_mal' ? item.label_ml || item.label : item.label
  }

  const getRoutePath = (slug?: string) => {
    if (!slug) return '/'
    return router_config[slug]?.path || '/'
  }

  const filteredNavItems = navItems.map((item) => {
    if (!item.children) return item
    const filteredChildren = item.children.filter((child) => {
      if (child.slug === 'REDRESSAL' && !authenticated) return false
      return true
    })
    return { ...item, children: filteredChildren }
  })

  // Dropdown & toggle handlers (same as before)
  const handleDropdownToggle = (index: number) => {
    setOpenDropdownIndex(openDropdownIndex === index ? null : index)
    setOpenChildIndex({})
    setShowUserMenu(false)
  }

  const handleMobileParentToggle = (index: number) => {
    setExpandedParentIndex(expandedParentIndex === index ? null : index)
    setExpandedChildIndex(null)
    setShowUserMenu(false)
  }

  const handleChildDropdownToggle = (
    parentIndex: number,
    childIndex: number
  ) => {
    setOpenChildIndex((prev) => ({
      ...prev,
      [parentIndex]: prev[parentIndex] === childIndex ? null : childIndex,
    }))
  }

  const handleMobileChildToggle = (childIndex: number) => {
    setExpandedChildIndex(expandedChildIndex === childIndex ? null : childIndex)
  }

  const handleUserMenuToggle = () => {
    setShowUserMenu(!showUserMenu)
    setOpenDropdownIndex(null)
    setExpandedParentIndex(null)
    setExpandedChildIndex(null)
    setOpenChildIndex({})
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenDropdownIndex(null)
        setExpandedParentIndex(null)
        setExpandedChildIndex(null)
        setOpenChildIndex({})
      }
      if (
        userIconRef.current &&
        !userIconRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <header className="sticky top-0 z-20 bg-white">
      <div className="w-11/12 mx-auto flex justify-between items-center py-4">
        {/* Logos */}
        <div className="text-2xl font-extrabold text-red-600 tracking-wide flex gap-3">
          <img
            src={govtLogo}
            className="lg:w-[6.25rem] md:w-32 w-24"
            alt="Govt Logo"
          />
          <Link to="/home">
            <img
              src={EdcLogo}
              className="lg:w-[8rem] md:w-32 w-24 cursor-pointer"
              alt="EDC Logo"
            />
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <div className="lg:hidden">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-red-600 focus:outline-none relative w-8 h-8"
          >
            <span
              className={`absolute inset-0 transition-opacity ${mobileMenuOpen ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
            >
              <HamburgerIcon />
            </span>
            <span
              className={`absolute inset-0 transition-opacity ${mobileMenuOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
            >
              <Close />
            </span>
          </button>
        </div>

        {/* Navigation */}
        <nav
          ref={navRef}
          className={`${mobileMenuOpen ? 'block' : 'hidden'} lg:flex flex-col lg:flex-row lg:gap-6 absolute lg:static top-full left-0 w-full lg:items-center lg:w-auto bg-white lg:bg-transparent shadow-md lg:shadow-none p-4 lg:p-0 z-40`}
        >
          {filteredNavItems.map((item, i) => (
            <div key={i} className="group relative lg:mx-3 mb-3 lg:mb-0">
              {item.slug ? (
                <Link
                  to={getRoutePath(item.slug)}
                  className="font-regular text-PrimaryGray hover:text-primaryBlue transition"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {getLabel(item)}
                </Link>
              ) : (
                <button
                  onClick={() =>
                    window.innerWidth < 1024
                      ? handleMobileParentToggle(i)
                      : handleDropdownToggle(i)
                  }
                  className="font-regular text-PrimaryGray hover:text-primaryBlue transition w-full text-left flex items-center gap-1"
                >
                  {getLabel(item)}
                  <span>
                    {(
                      window.innerWidth < 1024
                        ? expandedParentIndex === i
                        : openDropdownIndex === i
                    ) ? (
                      <UpArrow />
                    ) : (
                      <DownArrow />
                    )}
                  </span>
                </button>
              )}

              {/* Desktop Dropdown */}
              {item.children && openDropdownIndex === i && (
                <div className="hidden lg:block absolute left-0 top-full mt-2 bg-white shadow-lg rounded-md min-w-[180px] z-50">
                  {item.children.map((child, j) => (
                    <div key={j} className="relative group">
                      {child.slug ? (
                        <Link
                          to={getRoutePath(child.slug)}
                          className="w-full text-left px-4 py-2 text-sm font-medium text-LightGray flex justify-between items-center"
                          onClick={() => setOpenDropdownIndex(null)}
                        >
                          {getLabel(child)}
                        </Link>
                      ) : (
                        <button
                          onClick={() => handleChildDropdownToggle(i, j)}
                          className="w-full text-left px-4 py-2 text-sm font-medium text-LightGray flex justify-between items-center"
                        >
                          {getLabel(child)}
                          {child.subChildren && (
                            <span className="ml-2">
                              {openChildIndex[i] === j ? (
                                <UpArrow />
                              ) : (
                                <DownArrow />
                              )}
                            </span>
                          )}
                        </button>
                      )}

                      {child.subChildren && openChildIndex[i] === j && (
                        <div className="absolute left-full top-0 ml-1 bg-white shadow-lg rounded-md min-w-[160px] z-50">
                          {child.subChildren.map((sub, k) => (
                            <Link
                              key={k}
                              to={getRoutePath(sub.slug)}
                              className="block px-4 py-2 text-sm text-PrimaryGray"
                              onClick={() => setOpenDropdownIndex(null)}
                            >
                              {getLabel(sub)}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Mobile Tree */}
              {item.children && expandedParentIndex === i && (
                <div className="lg:hidden pl-4 mt-2 border-l border-red-100">
                  {item.children.map((child, j) => (
                    <div key={j}>
                      {child.slug ? (
                        <Link
                          to={getRoutePath(child.slug)}
                          className="block w-full text-left text-sm text-gray-800 font-medium py-1 hover:text-red-500"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {getLabel(child)}
                        </Link>
                      ) : (
                        <>
                          <button
                            onClick={() => handleMobileChildToggle(j)}
                            className="block w-full text-left text-sm text-gray-800 font-medium py-1 hover:text-red-500 flex justify-between items-center"
                          >
                            {getLabel(child)}
                            {child.subChildren && (
                              <span className="ml-2">
                                {expandedChildIndex === j ? (
                                  <UpArrow />
                                ) : (
                                  <DownArrow />
                                )}
                              </span>
                            )}
                          </button>
                          {child.subChildren && expandedChildIndex === j && (
                            <div className="ml-4 mt-1 border-l border-gray-200 pl-3">
                              {child.subChildren.map((sub, k) => (
                                <Link
                                  key={k}
                                  to={getRoutePath(sub.slug)}
                                  className="block text-sm text-PrimaryGray py-1 hover:text-red-500"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  {getLabel(sub)}
                                </Link>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div className="lg:hidden flex flex-col space-y-1">
            {authenticated ? (
              <>
                <button
                  className="text-sm text-blue-600 underline hover:text-blue-800 text-left"
                  onClick={() => {
                    setMobileMenuOpen(false)
                    navigate('/dashboard')
                  }}
                >
                  Dashboard
                </button>
                <button
                  className="text-sm text-blue-600 underline hover:text-blue-800 text-left"
                  onClick={() => {
                    setMobileMenuOpen(false)
                    localStorage.clear()
                    window.location.href = '/'
                  }}
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm text-blue-600 underline hover:text-blue-800"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/registration"
                  className="text-sm text-blue-600 underline hover:text-blue-800"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Register
                </Link>
              </>
            )}
          </div>

          {/* User Icon */}
          <div className="lg:pl-10 lg:block hidden relative" ref={userIconRef}>
            <button onClick={handleUserMenuToggle}>
              <UserIcon />
            </button>
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-80 bg-white border rounded-md shadow-lg z-50 p-4 text-center">
                {authenticated ? (
                  <div>
                    <button
                      className="block w-full px-4 py-2 text-sm text-white bg-primaryBlue rounded"
                      onClick={() => {
                        setShowUserMenu(false)
                        navigate('/dashboard')
                      }}
                    >
                      Dashboard
                    </button>
                    <button
                      className="block w-full px-4 py-2 text-sm text-white bg-red-500 rounded mt-3"
                      onClick={() => {
                        localStorage.clear()
                        window.location.href = '/'
                      }}
                    >
                      Logout
                    </button>
                  </div>
                ) : (
                  <div className="border p-4 border-gray-200">
                    <p className="mb-4 text-sm text-gray-600">
                      Log in to continue or sign up for full access.
                    </p>
                    <div className="flex justify-center gap-4">
                      <Link to="/login" className="w-full">
                        <button className="w-full px-4 py-2 text-sm font-semibold text-white bg-PrimaryOrange rounded">
                          Log In
                        </button>
                      </Link>
                      <Link to="/registration" className="w-full">
                        <button className="w-full px-4 py-2 text-sm font-semibold text-white bg-PrimaryOrange rounded">
                          Register
                        </button>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </nav>
      </div>
    </header>
  )
}

export default MiddleHeader
